import React, { useState, useEffect } from 'react'
import { Card, Form, Input, Button, Alert, Badge, Progress } from 'antd'
import { useAuthMfaSecure } from 'providers/AuthSecureManage/AuthMfaSecureManage'
import styles from './MfaVerification.module.css'

const MfaVerification = ({ factors, challengeId, userEmail, onSuccess, onCancel }) => {
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [timeLeft, setTimeLeft] = useState(60)
  const [currentFactor] = useState(factors[0]) // Usar el primer factor disponible
  const [currentChallengeId, setCurrentChallengeId] = useState(challengeId)
  const isRappiEmail = userEmail && userEmail.toLowerCase().includes('rappi')
  
  const { verifyChallengeMFA, createChallengeMFA } = useAuthMfaSecure();

  useEffect(() => {
    window.history.pushState(null, '', window.location.href)
    
    const handlePopState = (event) => {
      window.history.pushState(null, '', window.location.href)
      setError('No puede regresar durante la verificación MFA. Use el botón "Cancelar" para volver al login.')
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  const createNewChallenge = async () => {
    try {
      setLoading(true)
      setError('')
      const newChallenge = await createChallengeMFA(currentFactor.id)
      setCurrentChallengeId(newChallenge.id)
      setTimeLeft(60)
      console.log('🔄 Nuevo challenge creado para verificación obligatoria:', newChallenge.id)
    } catch (error) {
      setError('Error creando challenge: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          createNewChallenge()
          return 60
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [currentFactor?.id])

  const handleVerify = async (values) => {
    const { code: formCode } = values

    try {
      setLoading(true)
      setError('')

      // Usar el challengeId actual para verificar y establecer sesión completa
      await verifyChallengeMFA(currentFactor.id, formCode || code, currentChallengeId)
      console.log('🎉 Verificación MFA obligatoria exitosa - usuario COMPLETAMENTE autenticado')
      onSuccess()
    } catch (error) {
      console.error('Error en verificación MFA obligatoria:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  if (!currentFactor) {
    return (
      <div className={styles.container}>
        <div className={styles.formContainer}>
          <Card style={{ borderRadius: 12 }}>
            <div className={styles.errorContainer}>
              <div className={styles.errorIcon}>❌</div>
              <h2 className={styles.errorTitle}>
                Error de Configuración
              </h2>
              <p className={styles.errorSubtitle}>
                No se encontraron factores MFA válidos
              </p>
              <Button
                type="primary"
                size="large"
                onClick={onCancel}
                style={{ marginTop: '1rem' }}
              >
                Volver al Login
              </Button>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            Verificación de Seguridad 🔐
          </h2>
          <p className={styles.subtitle}>
            Configuración exitosa. Ahora ingrese el código actual de su aplicación autenticadora para acceder
          </p>

        </div>


        <Card style={{ borderRadius: 12 }}>
          <Form onFinish={handleVerify} layout="vertical">
            {error && (
              <Alert
                message={error}
                type="error"
                icon={<span>❌</span>}
                showIcon
                style={{ marginBottom: '1rem' }}
              />
            )}

            {isRappiEmail && (
              <Alert
                message="Aviso Importante - Uso Interno de Rappi"
                description="Este portal está destinado únicamente para uso interno de Rappi. Las credenciales son de carácter individual y no deben ser compartidas con terceros bajo ninguna circunstancia."
                type="warning"
                icon={<span>⚠️</span>}
                showIcon
                style={{ marginBottom: '1rem' }}
              />
            )}

            <Alert
              message="¡Configuración MFA Completada!"
              description="Su autenticación de dos factores ha sido configurada exitosamente. Ahora ingrese el código actual de 6 dígitos que aparece en su aplicación autenticadora para completar el inicio de sesión."
              type="success"
              icon={<span>✅</span>}
              showIcon
              style={{ marginBottom: '1rem' }}
            />

            <Form.Item
              name="code"
              label="Código de verificación de 6 dígitos"
              rules={[
                { required: true, message: 'Ingrese el código' },
                { pattern: /^\d{6}$/, message: 'Debe ser un código de exactamente 6 dígitos' }
              ]}
            >
              <Input
                className={styles.codeInput}
                placeholder="000000"
                maxLength={6}
                value={code}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(value);
                }}
                disabled={loading}
                autoFocus
                size="large"
              />

            </Form.Item>
            <div className={styles.codeHelp}>
                El código cambia cada 30 segundos. Use el código actual que aparece en su aplicación
              </div>
            <div className={styles.buttonGroup}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loading}
              >
                {loading ? 'Verificando...' : 'Verificar y Acceder'}
              </Button>

              <Button
                size="large"
                block
                onClick={onCancel}
              >
                Cancelar y Volver al Login
              </Button>
            </div>
          </Form>
        </Card>

      </div>
    </div>
  )
}

export default MfaVerification